import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ASSET_IDS_LIVE } from "../../pages/Assets/assets";
import { Menu, MenuItem, Sidebar, SubMenu } from "react-pro-sidebar";
import { decodeToken } from "react-jwt";
import {
  HiOutlineArrowsRightLeft,
  HiOutlineBanknotes,
  HiOutlineBookOpen,
  HiOutlineBriefcase,
  HiOutlineBuildingOffice2,
  HiOutlineClipboardDocumentCheck,
  HiOutlineCalendarDays,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineClipboardDocumentList,
  HiOutlineTruck,
  HiOutlineClock,
  HiOutlineComputerDesktop,
  HiOutlineCodeBracket,
  HiOutlineCog6Tooth,
  HiOutlineCommandLine,
  HiOutlineCreditCard,
  HiOutlineTicket,
  HiOutlineCube,
  HiOutlineDocumentDuplicate,
  HiOutlineDocumentText,
  HiOutlineEnvelope,
  HiOutlineFire,
  HiOutlineFingerPrint,
  HiOutlineGlobeAlt,
  HiOutlineGlobeAmericas,
  HiOutlineBolt,
  HiOutlineIdentification,
  HiOutlineKey,
  HiOutlineMagnifyingGlass,
  HiOutlineMegaphone,
  HiOutlineNewspaper,
  HiOutlinePencilSquare,
  HiOutlinePuzzlePiece,
  HiOutlineQrCode,
  HiOutlineQuestionMarkCircle,
  HiOutlineReceiptPercent,
  HiOutlineScale,
  HiOutlineSquares2X2,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineUserCircle,
  HiOutlineUserGroup,
  HiOutlineUserPlus,
  HiOutlineUsers,
  HiOutlineWallet,
} from "react-icons/hi2";
import { hasFeatureAccess, isOomeroEnterpriseAdmin } from "../../utils/permissions";
import { DASHBOARD_LABEL } from '../../utils/branding';
import { getOpenAlerts, subscribeToOpenAlerts } from '../../utils/monitoringPulse';
import "./Sidebar.scss";

const Ico = ({ icon: Icon }) => <Icon size={18} aria-hidden="true" />;

const toBoolean = (...values) =>
  values.some(
    (value) =>
      value === true || value === "true" || value === 1 || value === "1",
  );


const SectionLabel = ({ collapsed, children }) => (
  <div className={collapsed ? "sidebar-section-label is-collapsed" : "sidebar-section-label"} aria-hidden="true">
    {collapsed ? null : children}
  </div>
);

// Production release 28 Sep 2026: company features stay hidden on the live site, as they were
// (Company Overview, Corporate Screening, onboarded Companies). Set to true to release them.
const SHOW_COMPANY_FEATURES = false;

function NewSidebar(props) {
  const location = useLocation();
  const parentMenu = window.location.href.split("/")[3];
  const [menuCollapse, setMenuCollapse] = useState(() => window.innerWidth < 768);
  const [showSidebar, setShowSidebar] = useState(true);
  const [openAlerts, setOpenAlerts] = useState(getOpenAlerts);
  useEffect(() => subscribeToOpenAlerts(setOpenAlerts), []);
  const hasOpenAlerts = Number(openAlerts) > 0;
  const userToken = localStorage.getItem("token");
  const decodedToken = useMemo(() => decodeToken(userToken) || {}, [userToken]);
  const rolePermissions = decodedToken?.roles?.permissions || {};
  const roleName = String(decodedToken?.roles?.role_name || decodedToken?.role_name || "").toLowerCase();

  const access = useMemo(() => {
    const isSuperAdmin = toBoolean(decodedToken.superAdmin, decodedToken.isSuperAdmin, decodedToken.is_superadmin);
    const isEnterpriseAdmin = toBoolean(decodedToken.isEnterpriseAdmin, decodedToken.is_enterprise_admin);
    const isOomero = toBoolean(decodedToken.isOomero, decodedToken.is_oomero);
    const isAuditor = toBoolean(decodedToken.isAuditor, decodedToken.is_auditor) || roleName === "auditor";
    // A gold refinery (entity industry "Gold Refinery", see oomero-be utils/refineryIndustry.js).
    const isRefinery = toBoolean(decodedToken.isRefinery, decodedToken.is_refinery);
    const permission = (name) => Boolean(rolePermissions[name]);

    const canManageAuditors = isSuperAdmin || isEnterpriseAdmin || isOomero || roleName === "mlro" || permission("create_ghost_role");
    const canViewIndividualScreening = isSuperAdmin || isEnterpriseAdmin || permission("view_individual_screening_list") || (isAuditor && permission("auditors_check_individual_screening"));
    const canViewCorporateScreening = SHOW_COMPANY_FEATURES && (isSuperAdmin || isEnterpriseAdmin || permission("view_corporate_screening") || (isAuditor && permission("auditors_check_corporate_screening")));
    const canViewOnboardedIndividuals = isSuperAdmin || isEnterpriseAdmin || permission("view_onboarded_individual_profiles") || (isAuditor && permission("auditors_check_onboarded_individuals"));
    const canViewOnboardedCorporates = SHOW_COMPANY_FEATURES && (isSuperAdmin || isEnterpriseAdmin || permission("view_onboarded_corporate_profiles") || permission("view_onboarded_individual_profiles") || (isAuditor && permission("auditors_check_onboarded_corporates")));
    const canViewEmailSettings = isSuperAdmin || permission("view_email_master") || permission("view_email_master_list");
    const canViewEmailLog = isSuperAdmin || isEnterpriseAdmin || permission("view_email_log") || canViewEmailSettings;
    const canViewConsentLog = isSuperAdmin || isEnterpriseAdmin || permission("view_consent_log");
    const canViewUserLog = isSuperAdmin || isEnterpriseAdmin || permission("view_user_log");
    const canManageClientAdministration = isSuperAdmin || (isOomero && [
      "setup_new_client",
      "view_client_list",
      "view_client_details",
      "edit_client",
    ].some(permission));

    return {
      isSuperAdmin,
      isEnterpriseAdmin,
      isOomero,
      isAuditor,
      canViewDashboard: isSuperAdmin || isEnterpriseAdmin || permission("view_dashboard"),
      canManageAuditors,
      canViewIndividualScreening,
      canViewCorporateScreening,
      canViewOnboardedIndividuals,
      canViewOnboardedCorporates,
      canViewAml: isSuperAdmin || isEnterpriseAdmin || permission("view_aml_check_menu"),
      // Oomero Asset IDs (meeting 29 Sep 2026): issuers and Oomero, who reviews them.
      // Not live yet (12 Oct 2026 meeting): the Oomero team and refineries only, not other client companies such as the Kutana demo.
      canUseAssets: !isAuditor && (ASSET_IDS_LIVE || isSuperAdmin || isOomero || isRefinery) && (isSuperAdmin || isEnterpriseAdmin || isOomero || permission("create_asset_onboarding")),
      canCreateProfile: isSuperAdmin || isEnterpriseAdmin || permission("create_individual_onboarding") || permission("create_business_onboarding") || permission("create_asset_onboarding") || permission("onboard_using_oomero_id"),
      canManageUsers: isSuperAdmin || isEnterpriseAdmin || permission("view_oomero_user") || permission("view_client_user") || permission("view_user_role"),
      canViewUsers: isSuperAdmin || isEnterpriseAdmin || permission("view_oomero_user") || permission("view_client_user"),
      canViewRoles: isSuperAdmin || isEnterpriseAdmin || permission("view_user_role"),
      canViewLogs: !isAuditor && (isSuperAdmin || isEnterpriseAdmin || isOomero || canManageAuditors || canViewUserLog || canViewEmailLog || canViewConsentLog),
      canViewUserLog,
      canViewEmailLog,
      canViewConsentLog,
      canViewEmailSettings,
      canManageClientAdministration,
      canManageServiceSlabs: isSuperAdmin || (isOomero && permission("manage_service_slabs")),
      // A company's enterprise admin can open Developer tools to request API access (2 Oct 2026);
      // there each user sees only their own keys and usage. Others need the permission.
      canViewDeveloperApi: isSuperAdmin || isEnterpriseAdmin || permission("view_developer_api"),
      canViewFxRates: isSuperAdmin || (isOomero && permission("fetch_fx_rate")),
      canViewTaxSettings: isSuperAdmin || (isOomero && permission("view_tax")),
      canManageClientRekycTime: hasFeatureAccess(decodedToken, ["manage_client_rekyc_settings"], { allowEnterpriseAdmin: true }),
      canViewGovernance: (isSuperAdmin || isOomero) && hasFeatureAccess(decodedToken, ["view_governance", "manage_governance", "manage_governance_purge"]),
      canViewWorkflowBuilder: !isAuditor && hasFeatureAccess(decodedToken, ["manage_workflows", "view_workflows"], { allowEnterpriseAdmin: true, allowOomero: true }),
      // Refinery workspace: only for gold refineries (and Oomero staff, who set them up).
      isRefinery,
      canViewRefinery: !isAuditor && (isSuperAdmin || isOomero || (isRefinery && (isEnterpriseAdmin || canViewCorporateScreening || canViewOnboardedCorporates || canViewOnboardedIndividuals || canViewIndividualScreening))),
      // Production hides company features, but a refinery keeps its own Refinery profile (14 Oct 2026).
      canViewCompanyOverview: (SHOW_COMPANY_FEATURES || isRefinery) && hasFeatureAccess(decodedToken, ["view_company_overview", "manage_company_overview"], { allowEnterpriseAdmin: true }),
      canManageSelfServicePlan: !isSuperAdmin && hasFeatureAccess(decodedToken, ["manage_self_service_plan"], { allowEnterpriseAdmin: true, allowOomero: true }),
      canManageFormTemplates: isOomeroEnterpriseAdmin(decodedToken),
      canViewBilling: isSuperAdmin || isEnterpriseAdmin || (isOomero && permission("view_client_billing_menu")) || ["prepaid", "postpaid"].includes(decodedToken?.packageType),
      // Gateway payments are listed only in Payment History (28 Sep 2026), so Oomero staff, who
      // saw them under Settings > Payment Gateway, reach it without the billing menu permission.
      canViewPaymentHistory: isSuperAdmin || isEnterpriseAdmin || isOomero,
      permission,
    };
  }, [decodedToken, roleName, rolePermissions]);

  const isActive = (...paths) => paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const collapsedItemStyle = {
    display: menuCollapse ? "grid" : "flex",
    justifyItems: menuCollapse ? "center" : undefined,
  };

  useEffect(() => {
    setShowSidebar(parentMenu !== "pricing");
  }, [parentMenu]);

  useEffect(() => {
    const compactNavigationForViewport = () => {
      if (window.innerWidth < 768) setMenuCollapse(true);
    };
    compactNavigationForViewport();
    window.addEventListener("resize", compactNavigationForViewport);
    return () => window.removeEventListener("resize", compactNavigationForViewport);
  }, []);

  const toggle = () => setMenuCollapse((current) => !current);

  // A refinery only has its own menu (meeting 29 Sep 2026); the other pages redirect to the
  // refinery ones (utils/refineryAccess.js).
  const refineryMode = access.isRefinery && !access.isSuperAdmin && !access.isOomero && !access.isAuditor;
  const canOnboardRefineryCustomers = access.isSuperAdmin || access.isEnterpriseAdmin || access.permission("create_business_onboarding");

  return (
    <>
      {showSidebar ? (
        <div
          className="leftapp"
          style={{
            width: menuCollapse ? "66px" : "216px",
            flexShrink: 0,
            position: "fixed",
            top: 58,
            left: 0,
            bottom: 0,
            zIndex: 520,
          }}
        >
          <Sidebar
            collapsed={menuCollapse}
            backgroundColor="#f6f8fc"
            collapsedWidth="66px"
            width="216px"
            breakPoint="md"
            className="app"
            style={{ position: "absolute", inset: 0, border: 0, height: "100%", zIndex: 500 }}
          >
            <div className="d-flex flex-column justify-content-between position-relative">
              <button type="button" onClick={toggle} className="sidebar-toggle-container" aria-label={menuCollapse ? "Expand navigation" : "Collapse navigation"}>
                {menuCollapse ? <Ico icon={HiOutlineChevronDoubleRight} /> : <Ico icon={HiOutlineChevronDoubleLeft} />}
              </button>

              <Menu
                className="sidebar-height"
                style={{ height: "calc(100vh - 58px)", overflowY: "auto", paddingBottom: "30px" }}
              >
                {refineryMode ? (
                  <>
                    <SectionLabel collapsed={menuCollapse}>Refinery</SectionLabel>
                    <MenuItem component={<Link to="/refinery" className="link" />} icon={<Ico icon={HiOutlineSquares2X2} />} active={location.pathname === "/refinery"} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Overview"}
                    </MenuItem>
                    {canOnboardRefineryCustomers ? (
                      <MenuItem component={<Link to="/refinery/onboard" className="link" />} icon={<Ico icon={HiOutlineUserPlus} />} active={isActive("/refinery/onboard")} style={collapsedItemStyle}>
                        {menuCollapse ? null : "Onboard a customer"}
                      </MenuItem>
                    ) : null}
                    <MenuItem component={<Link to="/refinery/onboardings" className="link" />} icon={<Ico icon={HiOutlineClipboardDocumentCheck} />} active={isActive("/refinery/onboardings")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Onboarding dashboard"}
                    </MenuItem>
                    <MenuItem component={<Link to="/refinery/clients" className="link" />} icon={<Ico icon={HiOutlineBuildingOffice2} />} active={isActive("/refinery/clients", "/refinery/company", "/refinery/individual")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Clients"}
                    </MenuItem>
                    <MenuItem component={<Link to="/refinery/shipments" className="link" />} icon={<Ico icon={HiOutlineTruck} />} active={isActive("/refinery/shipments")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Shipments"}
                    </MenuItem>
                    {(access.canViewOnboardedIndividuals || access.canViewOnboardedCorporates) ? (
                      <MenuItem component={<Link to="/reports/client-book" className="link" />} icon={<Ico icon={HiOutlineBookOpen} />} active={isActive("/reports/client-book")} style={collapsedItemStyle}>
                        {menuCollapse ? null : "Client book"}
                      </MenuItem>
                    ) : null}
                    {access.canUseAssets ? (
                      <MenuItem component={<Link to="/assets" className="link" />} icon={<Ico icon={HiOutlineQrCode} />} active={isActive("/assets")} style={collapsedItemStyle}>
                        {menuCollapse ? null : "Asset IDs"}
                      </MenuItem>
                    ) : null}
                    {access.canManageClientRekycTime ? (
                      <MenuItem component={<Link to="/refinery/settings" className="link" />} icon={<Ico icon={HiOutlineCog6Tooth} />} active={isActive("/refinery/settings")} style={collapsedItemStyle}>
                        {menuCollapse ? null : "Refinery settings"}
                      </MenuItem>
                    ) : null}
                    {access.canViewCompanyOverview ? (
                      <>
                        <SectionLabel collapsed={menuCollapse}>Company</SectionLabel>
                        <MenuItem component={<Link to="/company-overview" className="link" />} icon={<Ico icon={HiOutlineFire} />} active={isActive("/company-overview", "/company_overview", "/CompanyOverview")} style={collapsedItemStyle}>
                          {menuCollapse ? null : "Refinery profile"}
                        </MenuItem>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                  <SectionLabel collapsed={menuCollapse}>Workspace</SectionLabel>
                  {(access.isAuditor || access.canViewDashboard) ? (
                    <MenuItem
                      component={<Link to={access.isAuditor ? "/auditors_dashboard" : "/"} className="link" />}
                      icon={access.isAuditor ? <Ico icon={HiOutlineShieldCheck} /> : <Ico icon={HiOutlineBolt} />}
                      active={access.isAuditor ? isActive("/auditors_dashboard") : location.pathname === "/" || isActive("/dashboard")}
                      style={collapsedItemStyle}
                    >
                      {menuCollapse ? null : access.isAuditor ? "Auditor Workspace" : (
                        <>
                          <span
                            className={hasOpenAlerts ? "pulse-nav-label has-alerts" : "pulse-nav-label"}
                            title={hasOpenAlerts ? `${openAlerts} open alert${Number(openAlerts) === 1 ? "" : "s"}` : undefined}
                          >
                            {DASHBOARD_LABEL}
                          </span>
                          {/* The red colour and the dot are visual only. The count is also
                              read out, and stays mounted so the change is announced. */}
                          <span className="visually-hidden" aria-live="polite">
                            {hasOpenAlerts ? `, ${openAlerts} open alert${Number(openAlerts) === 1 ? "" : "s"}` : ""}
                          </span>
                        </>
                      )}
                    </MenuItem>
                  ) : null}

                  {!access.isAuditor && access.canViewCompanyOverview ? (
                    <MenuItem component={<Link to="/company-overview" className="link" />} icon={<Ico icon={HiOutlineBuildingOffice2} />} active={isActive("/company-overview", "/company_overview", "/CompanyOverview")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Company Overview"}
                    </MenuItem>
                  ) : null}

                  {(access.canViewIndividualScreening || access.canViewCorporateScreening || access.canViewOnboardedIndividuals || access.canViewOnboardedCorporates || access.canCreateProfile || access.canViewAml || access.canUseAssets) ? (
                    <SectionLabel collapsed={menuCollapse}>Compliance</SectionLabel>
                  ) : null}

                  {access.canCreateProfile && !access.isAuditor ? (
                    <MenuItem component={<Link to="/create_profile" className="link" />} icon={<Ico icon={HiOutlineUserPlus} />} active={isActive("/create_profile") || parentMenu === "profile"} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Create Profile"}
                    </MenuItem>
                  ) : null}

                  {access.canViewAml && !access.isAuditor ? (
                    <MenuItem component={<Link to="/aml-check" className="link" />} icon={<Ico icon={HiOutlineShieldExclamation} />} active={isActive("/aml-check")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "AML Check"}
                    </MenuItem>
                  ) : null}

                  {access.canUseAssets ? (
                    <MenuItem component={<Link to="/assets" className="link" />} icon={<Ico icon={HiOutlineQrCode} />} active={isActive("/assets")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Asset IDs"}
                    </MenuItem>
                  ) : null}

                  {(access.canViewIndividualScreening || access.canViewCorporateScreening) && !access.isAuditor ? (
                    <MenuItem component={<Link to="/open-cases-by-company" className="link" />} icon={<Ico icon={HiOutlineBriefcase} />} active={isActive("/open-cases-by-company")} style={collapsedItemStyle}>
                      {menuCollapse ? null : "Open Cases by Company"}
                    </MenuItem>
                  ) : null}

                  {(access.canViewIndividualScreening || access.canViewCorporateScreening) ? (
                    <SubMenu icon={<Ico icon={HiOutlineMagnifyingGlass} />} label={menuCollapse ? null : "Screening"} active={isActive("/individual_screening", "/corporate_screening")} style={collapsedItemStyle}>
                      {access.canViewIndividualScreening ? (
                        <MenuItem component={<Link to="/individual_screening" className="link" />} icon={<Ico icon={HiOutlineUserCircle} />} active={isActive("/individual_screening")}>Individual Screening</MenuItem>
                      ) : null}
                      {access.canViewCorporateScreening ? (
                        <MenuItem component={<Link to="/corporate_screening" className="link" />} icon={<Ico icon={HiOutlineBriefcase} />} active={isActive("/corporate_screening")}>Corporate Screening</MenuItem>
                      ) : null}
                    </SubMenu>
                  ) : null}

                  {/* CRM: compliance dashboard, SAR / STR, registers (Trello #272, #271, #274). */}
                  {!access.isAuditor ? (
                    <SubMenu icon={<Ico icon={HiOutlineShieldExclamation} />} label={menuCollapse ? null : "Compliance"} active={isActive("/crm/str-reports", "/crm/registers", "/device-records") || (location.pathname === "/" && location.search.includes("view=compliance"))} style={collapsedItemStyle}>
                      <MenuItem component={<Link to="/?view=compliance" className="link" />} icon={<Ico icon={HiOutlineSquares2X2} />} active={location.pathname === "/" && location.search.includes("view=compliance")}>Compliance Dashboard</MenuItem>
                      <MenuItem component={<Link to="/crm/str-reports" className="link" />} icon={<Ico icon={HiOutlineDocumentText} />} active={isActive("/crm/str-reports")}>SAR / STR</MenuItem>
                      <MenuItem component={<Link to="/crm/registers" className="link" />} icon={<Ico icon={HiOutlineClipboardDocumentList} />} active={isActive("/crm/registers")}>Registers</MenuItem>
                      {access.isSuperAdmin ? <MenuItem component={<Link to="/device-records" className="link" />} icon={<Ico icon={HiOutlineComputerDesktop} />} active={isActive("/device-records")}>Device Records</MenuItem> : null}
                    </SubMenu>
                  ) : null}

                  {(access.canViewOnboardedIndividuals || access.canViewOnboardedCorporates) ? (
                    <SubMenu icon={<Ico icon={HiOutlineIdentification} />} label={menuCollapse ? null : "Onboarded Profiles"} active={isActive("/view_individual_user", "/view_corporate", "/reports/client-book")} style={collapsedItemStyle}>
                      {access.canViewOnboardedIndividuals ? (
                        <MenuItem component={<Link to="/view_individual_user" className="link" />} icon={<Ico icon={HiOutlineUserGroup} />} active={isActive("/view_individual_user")}>Individuals</MenuItem>
                      ) : null}
                      {access.canViewOnboardedCorporates ? (
                        <MenuItem component={<Link to="/view_corporate" className="link" />} icon={<Ico icon={HiOutlineBuildingOffice2} />} active={isActive("/view_corporate")}>Companies</MenuItem>
                      ) : null}
                      {/* The whole client book as one report: account ref, KYC, review, status, risk, remarks. */}
                      <MenuItem component={<Link to="/reports/client-book" className="link" />} icon={<Ico icon={HiOutlineBookOpen} />} active={isActive("/reports/client-book")}>Client Book</MenuItem>
                    </SubMenu>
                  ) : null}

                  {access.canViewRefinery ? (
                    <>
                      <SectionLabel collapsed={menuCollapse}>Refinery</SectionLabel>
                      <SubMenu icon={<Ico icon={HiOutlineFire} />} label={menuCollapse ? null : "Refinery"} active={isActive("/refinery")} style={collapsedItemStyle}>
                        <MenuItem component={<Link to="/refinery" className="link" />} icon={<Ico icon={HiOutlineSquares2X2} />} active={location.pathname === "/refinery"}>Overview</MenuItem>
                        {(access.isSuperAdmin || access.isEnterpriseAdmin || access.permission("create_business_onboarding")) ? <MenuItem component={<Link to="/refinery/onboard" className="link" />} icon={<Ico icon={HiOutlineUserPlus} />} active={isActive("/refinery/onboard")}>Onboard a customer</MenuItem> : null}
                        <MenuItem component={<Link to="/refinery/onboardings" className="link" />} icon={<Ico icon={HiOutlineClipboardDocumentCheck} />} active={isActive("/refinery/onboardings")}>Onboarding dashboard</MenuItem>
                        <MenuItem component={<Link to="/refinery/clients" className="link" />} icon={<Ico icon={HiOutlineBuildingOffice2} />} active={isActive("/refinery/clients", "/refinery/company", "/refinery/individual")}>Clients</MenuItem>
                        <MenuItem component={<Link to="/refinery/shipments" className="link" />} icon={<Ico icon={HiOutlineTruck} />} active={isActive("/refinery/shipments")}>Shipments</MenuItem>
                        {access.canManageClientRekycTime ? <MenuItem component={<Link to="/refinery/settings" className="link" />} icon={<Ico icon={HiOutlineCog6Tooth} />} active={isActive("/refinery/settings")}>Refinery settings</MenuItem> : null}
                      </SubMenu>
                    </>
                  ) : null}

                  {access.isSuperAdmin ? (
                    <SubMenu icon={<Ico icon={HiOutlineClock} />} label={menuCollapse ? null : "Pending Applications"} active={isActive("/pending_biometrics", "/pending_application", "/admin/oomero-id-country-changes")} style={collapsedItemStyle}>
                      <MenuItem component={<Link to="/pending_biometrics" className="link" />} icon={<Ico icon={HiOutlineFingerPrint} />} active={isActive("/pending_biometrics")}>Biometrics</MenuItem>
                      <MenuItem component={<Link to="/pending_application" className="link" />} icon={<Ico icon={HiOutlineDocumentText} />} active={isActive("/pending_application")}>Applications</MenuItem>
                      <MenuItem component={<Link to="/admin/oomero-id-country-changes" className="link" />} icon={<Ico icon={HiOutlineArrowsRightLeft} />} active={isActive("/admin/oomero-id-country-changes")}>Oomero ID Changes</MenuItem>
                    </SubMenu>
                  ) : null}
                  </>
                )}

                {(access.canManageUsers || access.canViewLogs || access.canManageClientAdministration || access.canManageAuditors) && !access.isAuditor ? (
                  <SectionLabel collapsed={menuCollapse}>Administration</SectionLabel>
                ) : null}

                {access.canManageUsers && !access.isAuditor ? (
                  <SubMenu icon={<Ico icon={HiOutlineUsers} />} label={menuCollapse ? null : "Users & Roles"} active={isActive("/manage_users", "/user_roles", "/login-history")} style={collapsedItemStyle}>
                    {access.canViewUsers ? <MenuItem component={<Link to="/manage_users" className="link" />} icon={<Ico icon={HiOutlineUserGroup} />} active={isActive("/manage_users")}>Manage Users</MenuItem> : null}
                    {access.isSuperAdmin ? <MenuItem component={<Link to="/login-history" className="link" />} icon={<Ico icon={HiOutlineClock} />} active={isActive("/login-history")}>Login History</MenuItem> : null}
                    {access.canViewRoles ? <MenuItem component={<Link to="/user_roles" className="link" />} icon={<Ico icon={HiOutlineKey} />} active={isActive("/user_roles")}>User Roles</MenuItem> : null}
                  </SubMenu>
                ) : null}

                {/* Every log opens on the one Audit & Logs page (pages/Logs/LogsHub). */}
                {access.canViewLogs ? (
                  <MenuItem component={<Link to="/logs" className="link" />} icon={<Ico icon={HiOutlineClipboardDocumentList} />} active={isActive("/logs", "/auditor_logs", "/reports", "/user_log", "/email_history", "/consent_log")} style={collapsedItemStyle}>
                    {menuCollapse ? null : "Audit & Logs"}
                  </MenuItem>
                ) : null}
                {access.canViewGovernance ? (
                  <MenuItem component={<Link to="/governance" className="link" />} icon={<Ico icon={HiOutlineScale} />} active={isActive("/governance")} style={collapsedItemStyle}>
                    {menuCollapse ? null : "Governance"}
                  </MenuItem>
                ) : null}

                {(access.canManageClientAdministration || access.canManageServiceSlabs) ? (
                  <SubMenu icon={<Ico icon={HiOutlineBriefcase} />} label={menuCollapse ? null : "Administration"} active={isActive("/manage_new_client", "/self_service_service_slabs", "/self_service_assign_countries", "/add_oomero_company", "/oomero_companies", "/auditors_dashboard")} style={collapsedItemStyle}>
                    {access.canManageAuditors ? <MenuItem component={<Link to="/auditors_dashboard" className="link" />} icon={<Ico icon={HiOutlineShieldCheck} />} active={isActive("/auditors_dashboard")}>Auditor Management</MenuItem> : null}
                    {access.canManageClientAdministration && <MenuItem component={<Link to="/manage_new_client" className="link" />} icon={<Ico icon={HiOutlineUserPlus} />} active={isActive("/manage_new_client")}>Manage Clients</MenuItem>}
                    {(access.isSuperAdmin || access.permission("setup_new_client")) && <MenuItem component={<Link to="/add_oomero_company" className="link" />} icon={<Ico icon={HiOutlineBuildingOffice2} />} active={isActive("/add_oomero_company", "/oomero_companies")}>Company Setup</MenuItem>}
                    {access.canManageServiceSlabs ? <MenuItem component={<Link to="/self_service_service_slabs" className="link" />} icon={<Ico icon={HiOutlineSquares2X2} />} active={isActive("/self_service_service_slabs")}>Service Slabs</MenuItem> : null}
                    {access.isSuperAdmin ? <MenuItem component={<Link to="/self_service_assign_countries" className="link" />} icon={<Ico icon={HiOutlineGlobeAlt} />} active={isActive("/self_service_assign_countries")}>Assign Countries</MenuItem> : null}
                  </SubMenu>
                ) : !access.isAuditor && access.canManageAuditors ? (
                  <MenuItem component={<Link to="/auditors_dashboard" className="link" />} icon={<Ico icon={HiOutlineShieldCheck} />} active={isActive("/auditors_dashboard")} style={collapsedItemStyle}>
                    {menuCollapse ? null : "Auditor Management"}
                  </MenuItem>
                ) : null}

                {(access.canViewBilling || access.canViewPaymentHistory) && !access.isAuditor ? (
                  <>
                    <SectionLabel collapsed={menuCollapse}>Billing</SectionLabel>
                    <SubMenu icon={<Ico icon={HiOutlineCreditCard} />} label={menuCollapse ? null : "Billing & Payments"} active={isActive("/billing/self-service", "/wallet-dashboard", "/wallet_details", "/payment_history", "/tax-overview", "/client_biling", "/billing")} style={collapsedItemStyle}>
                      {access.canViewBilling && access.canManageSelfServicePlan ? <MenuItem component={<Link to="/billing/self-service" className="link" />} icon={<Ico icon={HiOutlineCube} />} active={isActive("/billing/self-service")}>Self-Service Pricing</MenuItem> : null}
                      {access.isSuperAdmin ? <MenuItem component={<Link to="/wallet-dashboard" className="link" />} icon={<Ico icon={HiOutlineWallet} />} active={isActive("/wallet-dashboard")}>Wallet Dashboard</MenuItem> : null}
                      {!access.isSuperAdmin && (access.isEnterpriseAdmin || ["prepaid", "postpaid"].includes(decodedToken?.packageType)) ? <MenuItem component={<Link to="/wallet_details" className="link" />} icon={<Ico icon={HiOutlineWallet} />} active={isActive("/wallet_details")}>Company Wallet</MenuItem> : null}
                      {access.canViewPaymentHistory ? <MenuItem component={<Link to="/payment_history" className="link" />} icon={<Ico icon={HiOutlineBanknotes} />} active={isActive("/payment_history")}>Payment History</MenuItem> : null}
                      {access.canViewBilling && (access.isSuperAdmin || access.isOomero) ? <MenuItem component={<Link to="/tax-overview" className="link" />} icon={<Ico icon={HiOutlineReceiptPercent} />} active={isActive("/tax-overview")}>Tax Overview</MenuItem> : null}
                      {(access.isSuperAdmin || (access.isOomero && access.permission("view_client_billing_menu")) || (!access.isEnterpriseAdmin && decodedToken?.packageType === "prepaid")) ? <MenuItem component={<Link to="/client_biling" className="link" />} icon={<Ico icon={HiOutlineDocumentDuplicate} />} active={isActive("/client_biling")}>Client Billing</MenuItem> : null}
                      {!access.isEnterpriseAdmin && decodedToken?.packageType === "postpaid" ? <MenuItem component={<Link to="/billing" className="link" />} icon={<Ico icon={HiOutlineNewspaper} />} active={isActive("/billing")}>Invoices</MenuItem> : null}
                    </SubMenu>
                  </>
                ) : null}

                <SectionLabel collapsed={menuCollapse}>Resources</SectionLabel>
                <SubMenu icon={<Ico icon={HiOutlineBookOpen} />} label={menuCollapse ? null : "Help"} active={isActive("/help", "/faq", "/geographic_risk", "/workflow-builder")} style={collapsedItemStyle}>
                  <MenuItem component={<Link to="/help" className="link" />} icon={<Ico icon={HiOutlineBookOpen} />} active={isActive("/help")}>Help home</MenuItem>
                  <MenuItem component={<Link to="/faq" className="link" />} icon={<Ico icon={HiOutlineQuestionMarkCircle} />} active={isActive("/faq") && !isActive("/faq/manage")}>FAQs</MenuItem>
                  <MenuItem component={<Link to="/help/procedure-manual" className="link" />} icon={<Ico icon={HiOutlineClipboardDocumentCheck} />} active={isActive("/help/procedure-manual")}>Procedure Manual</MenuItem>
                  {(access.isSuperAdmin || access.permission("manage_faqs")) ? <MenuItem component={<Link to="/faq/manage" className="link" />} icon={<Ico icon={HiOutlinePencilSquare} />} active={isActive("/faq/manage")}>Manage FAQs</MenuItem> : null}
                  <MenuItem component={<Link to="/geographic_risk" className="link" />} icon={<Ico icon={HiOutlineGlobeAmericas} />} active={isActive("/geographic_risk")}>FATF plus Know Your Country</MenuItem>
                  {access.canViewWorkflowBuilder ? <MenuItem component={<Link to="/workflow-builder" className="link" />} icon={<Ico icon={HiOutlineSquares2X2} />} active={isActive("/workflow-builder")}>Workflow builder</MenuItem> : null}
                </SubMenu>

                {access.canViewDeveloperApi && !access.isAuditor && !refineryMode ? (
                  <SubMenu icon={<Ico icon={HiOutlineCodeBracket} />} label={menuCollapse ? null : "Developer Tools"} active={isActive("/developer-api", "/crm-sdk", "/partners/propspace")} style={collapsedItemStyle}>
                    <MenuItem component={<Link to="/developer-api" className="link" />} icon={<Ico icon={HiOutlineCommandLine} />} active={isActive("/developer-api")}>Developer API</MenuItem>
                    <MenuItem component={<Link to="/crm-sdk" className="link" />} icon={<Ico icon={HiOutlinePuzzlePiece} />} active={isActive("/crm-sdk")}>CRM SDK</MenuItem>
                    {access.isSuperAdmin || access.isOomero ? <MenuItem component={<Link to="/partners/propspace" className="link" />} icon={<Ico icon={HiOutlineArrowsRightLeft} />} active={isActive("/partners/propspace")}>PropSpace AML check</MenuItem> : null}
                  </SubMenu>
                ) : null}

                <SubMenu icon={<Ico icon={HiOutlineCog6Tooth} />} label={menuCollapse ? null : "Settings"} active={isActive("/my_profile", "/form_templates_setup", "/settings/client-rekyc-time", "/settings/aml-risk", "/settings/compliance-risk", "/settings/risk-weightage", "/settings/ubo-threshold", "/settings/global-roles", "/fxrates", "/tax", "/email_masters", "/login-news/manage", "/settings/payment-gateway", "/settings/offer-codes")} style={collapsedItemStyle}>
                  <MenuItem component={<Link to="/my_profile" className="link" />} icon={<Ico icon={HiOutlineUserCircle} />} active={isActive("/my_profile")}>My Profile</MenuItem>
                  {access.canManageFormTemplates && !access.isAuditor ? <MenuItem component={<Link to="/form_templates_setup" className="link" />} icon={<Ico icon={HiOutlinePencilSquare} />} active={isActive("/form_templates_setup")}>Edit Forms</MenuItem> : null}
                  {access.canManageClientRekycTime && !access.isAuditor ? <MenuItem component={<Link to="/settings/client-rekyc-time" className="link" />} icon={<Ico icon={HiOutlineCalendarDays} />} active={isActive("/settings/client-rekyc-time")}>Client Re-KYC Time</MenuItem> : null}
                  {access.canManageClientRekycTime && !access.isAuditor ? <MenuItem component={<Link to="/settings/aml-risk" className="link" />} icon={<Ico icon={HiOutlineShieldCheck} />} active={isActive("/settings/aml-risk", "/settings/compliance-risk")}>AML Risk</MenuItem> : null}
                  {access.canManageClientRekycTime && !access.isAuditor ? <MenuItem component={<Link to="/settings/risk-weightage" className="link" />} icon={<Ico icon={HiOutlineScale} />} active={isActive("/settings/risk-weightage")}>Risk Weightage</MenuItem> : null}
                  {access.canManageClientRekycTime && !access.isAuditor ? <MenuItem component={<Link to="/settings/ubo-threshold" className="link" />} icon={<Ico icon={HiOutlineUserGroup} />} active={isActive("/settings/ubo-threshold")}>UBO Threshold</MenuItem> : null}
                  {(access.isSuperAdmin || access.isOomero) && !access.isAuditor ? <MenuItem component={<Link to="/settings/global-roles" className="link" />} icon={<Ico icon={HiOutlineKey} />} active={isActive("/settings/global-roles")}>Global Roles</MenuItem> : null}
                  {access.canViewFxRates && !access.isAuditor ? <MenuItem component={<Link to="/fxrates" className="link" />} icon={<Ico icon={HiOutlineArrowsRightLeft} />} active={isActive("/fxrates")}>FX Rates</MenuItem> : null}
                  {access.canViewTaxSettings && !access.isAuditor ? <MenuItem component={<Link to="/tax" className="link" />} icon={<Ico icon={HiOutlineReceiptPercent} />} active={isActive("/tax")}>Tax Rates</MenuItem> : null}
                  {access.canViewEmailSettings && !access.isAuditor ? <MenuItem component={<Link to="/email_masters" className="link" />} icon={<Ico icon={HiOutlineEnvelope} />} active={isActive("/email_masters")}>Email Masters</MenuItem> : null}
                  {access.isSuperAdmin ? <MenuItem component={<Link to="/login-news/manage" className="link" />} icon={<Ico icon={HiOutlineMegaphone} />} active={isActive("/login-news/manage")}>Login News</MenuItem> : null}
                  {(access.isSuperAdmin || access.isOomero) && !access.isAuditor ? <MenuItem component={<Link to="/settings/payment-gateway" className="link" />} icon={<Ico icon={HiOutlineCreditCard} />} active={isActive("/settings/payment-gateway")}>Payment Gateway</MenuItem> : null}
                  {(access.isSuperAdmin || access.isOomero) && !access.isAuditor ? <MenuItem component={<Link to="/settings/offer-codes" className="link" />} icon={<Ico icon={HiOutlineTicket} />} active={isActive("/settings/offer-codes")}>Offer Codes</MenuItem> : null}
                </SubMenu>
              </Menu>
            </div>
          </Sidebar>
        </div>
      ) : null}

      <div
        className="rightapp"
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "auto",
          marginLeft: showSidebar ? (menuCollapse ? "66px" : "216px") : 0,
          minHeight: "calc(100vh - 58px)",
        }}
      >
        <section>{props?.component ? <props.component /> : null}</section>
      </div>
    </>
  );
}

export default NewSidebar;
